import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { PageHeader } from '../components/PageHeader'
import { TabNavigation } from '../components/TabNavigation'
import { DashboardCard } from '../components/DashboardCard'

// Mock data - in real app this would come from API
const mockInvoiceData = {
  tuition: {
    'INV-2568-09-001': {
      id: 'INV-2568-09-001',
      month: 'กันยายน 2568',
      amount: 1600,
      due_date: '2025-09-01',
      invoice_date: '2025-08-25',
      type: 'tuition'
    }
  },
  expenses: {
    'EXP-001': {
      id: 'EXP-001',
      month: 'ค่าใช้จ่าย - ข้าวกล่อง + น้ำส้ม',
      amount: 55,
      due_date: '2025-08-31',
      invoice_date: '2025-08-17',
      type: 'expense'
    },
    'EXP-002': {
      id: 'EXP-002',
      month: 'ค่าใช้จ่าย - ขนม + โค้ก',
      amount: 50,
      due_date: '2025-08-31',
      invoice_date: '2025-08-16',
      type: 'expense'
    },
    'EXP-003': {
      id: 'EXP-003',
      month: 'ค่าใช้จ่าย - ดินสอ 2B + ยางลบ',
      amount: 50,
      due_date: '2025-08-31',
      invoice_date: '2025-08-15',
      type: 'expense'
    }
  },
  student: {
    name: 'น้องใส',
    student_code: 'STU-001'
  },
  bankInfo: {
    bank_name: 'ธนาคารกสิกรไทย',
    account_name: 'โรงเรียนกวดวิชาครูดรีม',
    account_number: '123-4-56789-0',
    promptpay_id: '0812345678'
  }
}

export const InvoiceDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [showFeedback, setShowFeedback] = useState<{ type: 'qr' | 'copy', message: string } | null>(null)

  // Find invoice from all sources
  const invoice = id ? (
    mockInvoiceData.tuition[id] || 
    mockInvoiceData.expenses[id]
  ) : null

  if (!invoice) {
    return (
      <div className="min-h-screen bg-gray-50 pb-20">
        <PageHeader 
          title="ไม่พบใบแจ้งหนี้"
          subtitle="ใบแจ้งหนี้ที่ท่านกำลังมองหาไม่พบในระบบ"
        />
        <div className="px-4 py-6">
          <DashboardCard title="ไม่พบข้อมูล">
            <div className="text-center py-8">
              <span className="text-4xl mb-4 block">❌</span>
              <p className="text-gray-600 mb-4">ไม่พบใบแจ้งหนี้ที่ท่านกำลังมองหา</p>
              <button 
                onClick={() => navigate(-1)}
                className="btn-primary"
              >
                ← กลับ
              </button>
            </div>
          </DashboardCard>
        </div>
        <TabNavigation />
      </div>
    )
  }

  // Utility functions
  const formatThaiDate = (dateString: string) => {
    const date = new Date(dateString)
    const thaiYear = date.getFullYear() + 543
    const months = [
      'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
      'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'
    ]
    return `${date.getDate()} ${months[date.getMonth()]} ${thaiYear}`
  }
  
  const formatThaiBaht = (amount: number) => {
    return `฿${new Intl.NumberFormat('th-TH').format(amount)}`
  }

  const handleSaveQR = () => {
    setShowFeedback({ type: 'qr', message: 'บันทึก QR Code สำเร็จ (จำลอง)' })
    setTimeout(() => setShowFeedback(null), 3000)
  }

  const handleCopyAccountNumber = () => {
    navigator.clipboard.writeText(mockInvoiceData.bankInfo.account_number)
    setShowFeedback({ type: 'copy', message: 'คัดลอกเลขบัญชีแล้ว' })
    setTimeout(() => setShowFeedback(null), 3000)
  }

  const handlePayment = () => {
    // Navigate to payment slip upload with the invoice data
    navigate(`/payment-slip/${invoice.id}`, { 
      state: { 
        amount: invoice.amount,
        returnPath: window.location.pathname
      } 
    })
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <PageHeader 
        title="📄 ใบแจ้งหนี้"
        subtitle={invoice.type === 'tuition' ? 'ค่าเรียนรายเดือน' : 'ค่าใช้จ่ายเพิ่มเติม'}
        actions={
          <button 
            onClick={() => navigate(-1)}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            ← กลับ
          </button>
        }
      />
      
      <div className="px-4 py-6 space-y-6">
        {/* Feedback Messages */}
        {showFeedback && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <p className="text-sm text-green-800 flex items-center">
              <span className="mr-2">✅</span>
              {showFeedback.message}
            </p>
          </div>
        )}
        
        {/* Invoice Header */}
        <DashboardCard title="รายละเอียดใบแจ้งหนี้" className="text-center border-b border-gray-200">
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            โรงเรียนกวดวิชาครูดรีม
          </h3>
          <p className="text-sm text-gray-600">ใบแจ้งหนี้ค่าเรียน</p>
        </DashboardCard>

        {/* Invoice Details */}
        <DashboardCard title="📋 รายละเอียดใบแจ้งหนี้">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">เลขที่:</span>
                <span className="font-medium ml-2">{invoice.id}</span>
              </div>
              <div>
                <span className="text-gray-600">วันที่:</span>
                <span className="font-medium ml-2">{formatThaiDate(invoice.invoice_date)}</span>
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-3 space-y-2">
              <h4 className="font-semibold text-gray-900">ข้อมูลนักเรียน</h4>
              <div className="text-sm space-y-1">
                <div>
                  <span className="text-gray-600">ชื่อ:</span>
                  <span className="font-medium ml-2">{mockInvoiceData.student.name}</span>
                </div>
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-3 space-y-2">
              <h4 className="font-semibold text-gray-900">รายละเอียดค่าเรียน</h4>
              <div className="text-sm space-y-1">
                <div>
                  <span className="text-gray-600">ประจำเดือน:</span>
                  <span className="font-medium ml-2">{invoice.month}</span>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="font-semibold text-gray-900">จำนวนเงินรวม:</span>
                <span className="text-2xl font-bold text-blue-600">
                  {formatThaiBaht(invoice.amount)}
                </span>
              </div>
              <div className="text-sm text-gray-600 mt-1">
                ครบกำหนด: {formatThaiDate(invoice.due_date)}
              </div>
            </div>
          </div>
        </DashboardCard>

        {/* Payment Methods */}
        <DashboardCard title="💳 ช่องทางการชำระเงิน">
          <div className="space-y-4">
            {/* QR Code */}
            <div className="text-center">
              <div className="inline-block p-4 bg-white border-2 border-gray-300 rounded-lg">
                <div className="w-48 h-48 bg-gray-100 flex items-center justify-center rounded">
                  {/* Mock QR Code Pattern */}
                  <div className="grid grid-cols-8 gap-1">
                    {Array.from({ length: 64 }, (_, i) => (
                      <div 
                        key={i} 
                        className={`w-2 h-2 ${
                          Math.random() > 0.5 ? 'bg-black' : 'bg-white'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                สแกน QR Code เพื่อโอนเงิน
              </p>
              <button 
                onClick={handleSaveQR}
                className="btn-secondary text-sm mt-2"
              >
                💾 บันทึก QR Code
              </button>
            </div>

            {/* Bank Details */}
            <div className="space-y-2 text-sm">
              <h5 className="font-medium text-gray-900">ข้อมูลบัญชีธนาคาร</h5>
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-600">ธนาคาร:</span>
                  <span className="font-medium">{mockInvoiceData.bankInfo.bank_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">ชื่อบัญชี:</span>
                  <span className="font-medium">{mockInvoiceData.bankInfo.account_name}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">เลขบัญชี:</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-medium">{mockInvoiceData.bankInfo.account_number}</span>
                    <button 
                      onClick={handleCopyAccountNumber}
                      className="text-blue-500 hover:text-blue-700 text-xs"
                    >
                      📋
                    </button>
                  </div>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">PromptPay:</span>
                  <span className="font-mono font-medium">{mockInvoiceData.bankInfo.promptpay_id}</span>
                </div>
              </div>
            </div>
          </div>
        </DashboardCard>

        {/* Important Notes */}
        <DashboardCard title="ข้อมูลการชำระเงิน" className="bg-orange-50 border-orange-200">
          <h4 className="font-semibold text-orange-900 mb-2">⚠️ ข้อสำคัญ</h4>
          <ul className="text-sm text-orange-800 space-y-1">
            <li>• ระบุรหัสนักเรียน <strong>{mockInvoiceData.student.student_code}</strong> ในหมายเหตุการโอน</li>
            <li>• อัพโหลดสลิปการโอนทันทีหลังชำระเงิน</li>
            <li>• ระบบจะตรวจสอบภายใน 2-4 ชั่วโมง (เวลาทำการ)</li>
            <li>• หากมีปัญหา ติดต่อ 081-XXX-XXXX</li>
          </ul>
        </DashboardCard>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-2">
          <button
            onClick={() => navigate(-1)}
            className="btn-secondary flex-1"
          >
            ← กลับ
          </button>
          <button
            onClick={handlePayment}
            className="btn-primary flex-1"
          >
            📸 ชำระเงินแล้ว
          </button>
        </div>
      </div>
      
      <TabNavigation />
    </div>
  )
}